(function () {
  const SELECTORS = {
    category: ".cpb-category",
    title: ".cpb-category-title",
    content: ".cpb-areas-gallery-images",
    swalTitle: "#swal2-title",
    swalHtml: "#swal2-html-container",
  };

  const initializedAttr = "data-fps-collapsible";
  const contentIdPrefix = "fps-category-content-";
  let uid = 0;
  let observer = null;
  let popupUpdateTimer = null;
  let previewScaleTimer = null;

  function getCategoryContent(category, title) {
    const nextElement = title.nextElementSibling;
    if (nextElement && nextElement.matches(SELECTORS.content)) {
      return nextElement;
    }

    return category.querySelector(SELECTORS.content);
  }

  function setCategoryState(category, isOpen) {
    const title = category.querySelector(SELECTORS.title);
    const content = category.querySelector(SELECTORS.content);

    if (!title || !content) return;

    category.classList.toggle("fps-cpb-category-open", isOpen);
    category.classList.toggle("fps-cpb-category-closed", !isOpen);
    title.setAttribute("aria-expanded", String(isOpen));
    content.hidden = !isOpen;

    if (isOpen) {
      content.style.removeProperty("display");
    } else {
      content.style.setProperty("display", "none", "important");
    }
  }

  function initCategory(category, shouldOpen = false) {
    if (category.getAttribute(initializedAttr) === "true") return;

    const title = category.querySelector(SELECTORS.title);
    if (!title) return;

    const content = getCategoryContent(category, title);
    if (!content) return;

    category.setAttribute(initializedAttr, "true");
    category.classList.add("fps-cpb-category-collapsible");

    const contentId = content.id || `${contentIdPrefix}${++uid}`;
    content.id = contentId;

    title.setAttribute("role", "button");
    title.setAttribute("tabindex", "0");
    title.setAttribute("aria-controls", contentId);

    if (!title.querySelector(".fps-cpb-category-arrow")) {
      const arrow = document.createElement("span");
      arrow.className = "fps-cpb-category-arrow";
      arrow.setAttribute("aria-hidden", "true");
      title.appendChild(arrow);
    }

    title.addEventListener("click", () => {
      setCategoryState(
        category,
        !category.classList.contains("fps-cpb-category-open")
      );
    });

    title.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      setCategoryState(
        category,
        !category.classList.contains("fps-cpb-category-open")
      );
    });

    setCategoryState(category, shouldOpen);
  }

  function initCollapsibleCategories(root = document) {
    const categories = Array.from(root.querySelectorAll(SELECTORS.category));
    categories.forEach((category, index) =>
      initCategory(category, index === 0)
    );
  }

  function findElement(root, selector) {
    if (root instanceof HTMLElement && root.matches(selector)) {
      return root;
    }

    return root.querySelector(selector);
  }

  function updateMissingPatchPopup(root = document) {
    const title = findElement(root, SELECTORS.swalTitle);
    const htmlContainer = findElement(root, SELECTORS.swalHtml);

    if (!title || !htmlContainer) return;
    if (title.textContent.trim() !== "Missing Printable Elements") return;

    const message = htmlContainer.textContent.trim();
    const missingCount = message.match(
      /Please add\s+(\d+)\s+more images? to area to continue\./i
    );

    if (!missingCount) return;

    const patchLabel = missingCount[1] === "1" ? "patch" : "patches";

    title.textContent = "Missing Patch Selections";
    htmlContainer.innerHTML = `<p>Please add ${missingCount[1]} ${patchLabel} to area to continue.</p>`;
  }

  function scheduleMissingPatchPopupUpdate() {
    window.clearTimeout(popupUpdateTimer);
    popupUpdateTimer = window.setTimeout(() => {
      updateMissingPatchPopup();
    }, 0);
  }

  function observeCategoryChanges() {
    if (observer) return;

    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "characterData") {
          scheduleMissingPatchPopupUpdate();
          schedulePreviewScaleUpdate();
          continue;
        }

        scheduleMissingPatchPopupUpdate();
        schedulePreviewScaleUpdate();

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches(SELECTORS.category)) {
            initCategory(node);
            return;
          }

          initCollapsibleCategories(node);
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function getPreviewOriginalSize(previewSticky) {
    const inlineWidth = parseFloat(previewSticky.style.width);
    const inlineHeight = parseFloat(previewSticky.style.height);

    return (
      Math.max(inlineWidth || 0, inlineHeight || 0) ||
      Math.max(
        previewSticky.scrollWidth || 0,
        previewSticky.scrollHeight || 0
      ) ||
      Math.max(previewSticky.offsetWidth || 0, previewSticky.offsetHeight || 0)
    );
  }

  function getPreviewOriginalDimensions(previewSticky) {
    const inlineWidth = parseFloat(previewSticky.style.width);
    const inlineHeight = parseFloat(previewSticky.style.height);

    const width =
      inlineWidth ||
      previewSticky.scrollWidth ||
      previewSticky.offsetWidth ||
      0;

    const height =
      inlineHeight ||
      previewSticky.scrollHeight ||
      previewSticky.offsetHeight ||
      width;

    return { width, height };
  }

  function updatePreviewScale() {
    const previewContainer = document.querySelector(".cpb-preview-container");
    const previewSticky = previewContainer?.querySelector(
      ".cpb-preview-sticky"
    );
    const previewElements = previewContainer?.querySelector(
      ".cpb-preview-elements"
    );

    if (!previewContainer || !previewSticky || !previewElements) return;

    if (!window.matchMedia("(min-width: 1000px)").matches) {
      previewContainer.style.removeProperty("--fps-cpb-preview-scale");
      previewContainer.style.removeProperty("--fps-cpb-preview-scaled-height");
      previewContainer.style.removeProperty("--fps-cpb-preview-offset-x");
      return;
    }

    const { width: originalWidth, height: originalHeight } =
      getPreviewOriginalDimensions(previewSticky);

    if (!originalWidth || !originalHeight) return;

    const previewTop = previewSticky.getBoundingClientRect().top;
    const bottomGap = 72;
    const availableHeight = window.innerHeight - previewTop - bottomGap;
    const availableWidth = previewContainer.getBoundingClientRect().width;

    if (availableHeight <= 0 || availableWidth <= 0) return;

    const scale = Math.min(
      1,
      availableHeight / originalHeight,
      availableWidth / originalWidth
    );

    const scaledHeight = originalHeight * scale;
    const offsetX = Math.max(0, (originalWidth - originalWidth * scale) / 2);

    previewContainer.style.setProperty(
      "--fps-cpb-preview-scale",
      scale.toFixed(3)
    );

    previewContainer.style.setProperty(
      "--fps-cpb-preview-scaled-height",
      `${scaledHeight}px`
    );

    previewContainer.style.setProperty(
      "--fps-cpb-preview-offset-x",
      `${offsetX}px`
    );
  }
  function schedulePreviewScaleUpdate(delay = 100) {
    window.clearTimeout(previewScaleTimer);

    previewScaleTimer = window.setTimeout(() => {
      updatePreviewScale();
    }, delay);
  }

  function runPreviewScaleUpdates() {
    schedulePreviewScaleUpdate(0);
    schedulePreviewScaleUpdate(100);

    window.setTimeout(updatePreviewScale, 300);
    window.setTimeout(updatePreviewScale, 700);
    window.setTimeout(updatePreviewScale, 1200);
    window.setTimeout(updatePreviewScale, 2000);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updatePreviewScale);
    });
  }

  function init() {
    initCollapsibleCategories();
    updateMissingPatchPopup();
    runPreviewScaleUpdates();
    observeCategoryChanges();
  }

  document.body.addEventListener("CPB_ON_PRODUCT_MOUNTED", function (event) {
    init();
  });

  window.addEventListener("load", runPreviewScaleUpdates);
  window.addEventListener("resize", runPreviewScaleUpdates);
  window.addEventListener("orientationchange", runPreviewScaleUpdates);

  document.body.addEventListener(
    "CPB_ON_UPDATE_CURRENT_TAB",
    runPreviewScaleUpdates
  );
  document.body.addEventListener(
    "CPB_ON_OPTIONS_UPDATE",
    runPreviewScaleUpdates
  );
  document.body.addEventListener(
    "CPB_ON_PRODUCT_UPDATED",
    runPreviewScaleUpdates
  );
})();
